// vzhlad_predajne_v2 (20. 10. 2026, ústava 61.10): starý oranžový panel/akcent nahradený hodnotami témy — 2 miest
// PouzivateliaTab.jsx — Users management (admin only)
// Master-detail layout: vľavo zoznam, vpravo profil + heslo + PIN + práva
// Backend: /api/auth/users (GET/POST/PUT/DELETE) + /api/auth/users/:id/action-pin
// Ústava: 63 (PIN 4 čísla) · 64.2 (admin only) · 69 (Users modul)

import React, { useEffect, useState, useMemo } from 'react'
import { useDirtyFlag } from '../hooks/useDirtyState.js'   // straz_v4 (29. 9. 2026): rozpísaný formulár drží spoločnú stráž odchodu (ústava 53.1.b)
import { COLORS, TYPO, SPACE, RADIUS, hladaj } from '../theme.js'
import { fetchAuth } from '../auth/fetchAuth.js'
import { useToast } from '../components/Toast.jsx'
import { useAuth } from '../auth/AuthContext.jsx'
import RowMenu from '../components/RowMenu.jsx'
import { SearchableSelect } from '../components/Ui.jsx'

const ROLE_LABELS = {
  admin:           'Admin',
  owner:           'Majiteľ',
  owner:           'Majiteľ (zápis)',
  owner_view:      'Majiteľ (čítanie)',
  office_manager:  'Office (Kancelária)',
  accountant:      'Back office',   // 24. 9. 2026 premenoval Mirko (práva konateľa)
  foreman:         'Majster',
  partak:          'Parťák',
  service:         'Marek (Sklad/Servis/Predajňa/Požičovňa)',
  shop:            'Predajňa',
  driver:          'Šofér',
  omietkar:        'Omietkár',
  poterar:         'Poterár',
  maliar:          'Maliar',
}

// Zoznam modulov pre individuálne práva (sek. 64.1 + 69)
// Format: { id, label, group }
const MODULES_ALL = [
  { id: 'home',        label: 'Matrix (dashboard)', group: 'Prehľad' },
  { id: 'zakazky',     label: 'Zákazky',            group: 'Zákazky' },
  { id: 'reklamacie',  label: 'Reklamácie',         group: 'Zákazky' },   // 7. 10. 2026: modul to rešpektuje (reklamacie_api.js)
  { id: 'rozpis',      label: 'Rozpis prác',        group: 'Zákazky' },
  { id: 'dennik',      label: 'Denník',             group: 'Zákazky' },
  { id: 'cp',          label: 'Cenové ponuky',      group: 'Komercia' },
  { id: 'objednavky',  label: 'Objednávky',         group: 'Komercia' },
  { id: 'partneri',    label: 'Partneri',           group: 'Komercia' },
  { id: 'plu',         label: 'Katalógy',           group: 'Komercia' },
  { id: 'fa',          label: 'Faktúry',            group: 'Financie' },
  { id: 'pokladna',    label: 'Pokladňa',           group: 'Financie' },
  { id: 'mzdy',        label: 'Mzdy',               group: 'Financie' },
  { id: 'zadrzne',     label: 'Zádržné',            group: 'Financie' },
  { id: 'zamestnanci', label: 'Zamestnanci',        group: 'Ľudia' },
  { id: 'partie',      label: 'Partie',             group: 'Ľudia' },
  { id: 'pozicovna',   label: 'Požičovňa',          group: 'Majetok' },
  { id: 'vozidla',     label: 'Vozový park',        group: 'Majetok' },
  { id: 'wip',         label: 'WIP dashboard',      group: 'Riadenie' },
  { id: 'trinity',     label: 'Trinity',            group: 'Riadenie' },
  { id: 'nastavenia',  label: 'Nastavenia',         group: 'Riadenie' },
]

// Parser nav_permissions JSON (môže byť string alebo objekt)
function parseNav(np) {
  if (!np) return {}
  if (typeof np === 'string') { try { return JSON.parse(np) } catch (_) { return {} } }
  return np || {}
}

// Ústava 63.3 — kto má PIN (omietkar/poterar/maliar nie)
const PIN_FORBIDDEN_ROLES = ['omietkar', 'poterar', 'maliar']

// Roly použiteľné v UI (omietkar/poterar/maliar zatial neexistujú — Mirko 2026-05-26)
const ROLES_USABLE = [
  'admin', 'owner', 'owner_view', 'office_manager', 'accountant',
  'foreman', 'service', 'shop', 'partak', 'driver',
]

export default function PouzivateliaTab() {
  const { user: me } = useAuth()
  const toast = useToast()
  const [users, setUsers] = useState([])
  const [selId, setSelId] = useState(null)
  const [search, setSearch] = useState('')
  const [filterRole, setFilterRole] = useState('all')
  const [showInactive, setShowInactive] = useState(false)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [draft, setDraft] = useState(null) // editovaný user
  const _d0 = useMemo(() => (draft ? JSON.stringify(draft) : ''), [draft && draft.id]); useDirtyFlag(!!draft && JSON.stringify(draft) !== _d0, { key: 'pouzivatel-uprava', discard: () => { setDraft(null) } })   // straz_v4: rozpísané = zmenené od výberu záznamu
  const [newUserOpen, setNewUserOpen] = useState(false)
  const [resetPwOpen, setResetPwOpen] = useState(false)
  const [pinDialog, setPinDialog] = useState(false)

  const nacitaj = () => {
    setLoading(true)
    fetchAuth('/api/auth/users')
      .then(r => r.ok ? r.json() : [])
      .then(d => {
        const arr = Array.isArray(d) ? d : []
        setUsers(arr)
        let open = null; try { open = new URLSearchParams(window.location.search).get('open') } catch (_) {}   // identita_v1
        if (open && arr.some(u => String(u.id) === String(open))) setSelId(Number(open))
        else if (!selId && arr.length) setSelId(arr[0].id)
      })
      .catch(() => toast.error('Načítanie zlyhalo'))
      .finally(() => setLoading(false))
  }
  useEffect(nacitaj, [])

  const filtered = useMemo(() => {
    return users
      .filter(u => showInactive || u.active === 1)
      .filter(u => filterRole === 'all' || u.role === filterRole)
      .filter(u => {
        if (!search.trim()) return true
        const q = search.toLowerCase()
        return hladaj((u.username || ''), q) || hladaj((u.display_name || ''), q)
      })
  }, [users, search, filterRole, showInactive])

  const selected = useMemo(() => users.find(u => u.id === selId), [users, selId])

  // Väzba konto ↔ zamestnanec z číselníka (ústava 13: 1 zamestnanec = 1 konto; 69; Mirko 13. 9. 2026 „väzba je nutná“ —
  // bez nej konto nevidí dochádzku ani mzdy; prezývky (Aďo, Dano) mapovanie pokazia, preto sa vyberá z číselníka)
  const [emps, setEmps] = useState([])
  useEffect(() => { fetchAuth('/api/employees').then(r => r.ok ? r.json() : []).then(a => setEmps(Array.isArray(a) ? a : [])).catch(() => {}) }, [])
  const empOptionsFor = (uid, cur) => {
    const taken = new Set(users.filter(u => u.id !== uid).map(u => u.employee_id).filter(Boolean))
    return emps.filter(e => e.id === cur || (!taken.has(e.id) && e.active !== 0 && e.active !== '0'))
      .map(e => ({ value: e.id, label: (`${e.firstName || ''} ${e.lastName || ''}`.trim() || e.name || e.id) + ' · ' + e.id + (e.payType ? ' · ' + e.payType : '') }))
  }

  // synchronizuj draft pri zmene výberu
  useEffect(() => {
    if (selected) {
      const np = parseNav(selected.nav_permissions)
      setDraft({ ...selected, extra_modules: np.extra_modules || [] })
    }
  }, [selected?.id])

  const dirty = useMemo(() => {
    if (!selected || !draft) return false
    const baseChanged = ['username', 'display_name', 'role', 'active', 'desktop_enabled', 'phone', 'employee_id'].some(k => (selected[k] ?? null) !== (draft[k] ?? null))
    const origExtra = (parseNav(selected.nav_permissions).extra_modules || []).slice().sort().join(',')
    const newExtra = (draft.extra_modules || []).slice().sort().join(',')
    return baseChanged || origExtra !== newExtra
  }, [selected, draft])

  if (me?.role !== 'admin') {
    return (
      <div style={{ padding: SPACE.xl, textAlign: 'center', color: COLORS.muted }}>
        <div style={{ fontSize: TYPO.sizes.lg, fontWeight: TYPO.weights.bold, marginBottom: SPACE.sm }}>
          Prístup len pre Admin
        </div>
        <div style={{ fontSize: TYPO.sizes.sm }}>
          Správa používateľov je vyhradená pre admin rolu (ústava 63.3 + 64.2).
        </div>
      </div>
    )
  }

  const ulozit = async () => {
    if (!draft || !dirty) return
    setBusy(true)
    try {
      const r = await fetchAuth(`/api/auth/users/${draft.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          username: draft.username,
          display_name: draft.display_name,
          phone: draft.phone || '',
          employee_id: draft.employee_id || null,
          role: draft.role,
          active: draft.active,
          desktop_enabled: draft.desktop_enabled,
          // SPRAVA_APPIEK_V1: ostatné kľúče práv (hidden_modules zo Správy appiek) sa pri uložení profilu nesmú stratiť
          nav_permissions: { ...parseNav(selected.nav_permissions), extra_modules: draft.extra_modules || [] },
        }),
      })
      const d = await r.json()
      if (!r.ok) { toast.error(d.error || 'Uloženie zlyhalo'); setBusy(false); return }
      toast.success('Profil uložený')
      nacitaj()
    } catch (e) {
      toast.error('Chyba: ' + e.message)
    } finally { setBusy(false) }
  }

  const zmazat = async () => {
    if (!draft) return
    if (draft.id === me.id) { toast.error('Nemôžeš si deaktivovať vlastný účet'); return }
    if (!window.confirm(`Naozaj deaktivovať používateľa "${draft.username}"?\n\nÚčet sa zachová (mäkké zmazanie), len sa zablokuje prihlásenie.`)) return
    setBusy(true)
    try {
      const r = await fetchAuth(`/api/auth/users/${draft.id}`, { method: 'DELETE' })
      const d = await r.json()
      if (!r.ok) { toast.error(d.error || 'Zmazanie zlyhalo'); setBusy(false); return }
      toast.success('Účet deaktivovaný')
      nacitaj()
    } catch (e) {
      toast.error('Chyba: ' + e.message)
    } finally { setBusy(false) }
  }

  const styles = {
    wrap: { display: 'flex', gap: SPACE.md, minHeight: 600 },
    master: { width: 320, flexShrink: 0, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
    detail: { flex: 1, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, padding: SPACE.lg, background: COLORS.bg },
    toolbar: { padding: SPACE.sm, background: COLORS.bgSoft, borderBottom: `1px solid ${COLORS.line}` },
    listItem: (active) => ({
      padding: '10px 12px',
      borderBottom: `1px solid ${COLORS.line}`,
      cursor: 'pointer',
      background: active ? COLORS.navBg : 'transparent',
      borderLeft: active ? `3px solid ${COLORS.add}` : '3px solid transparent',
    }),
    label: { fontSize: TYPO.sizes.xs, color: COLORS.muted, marginBottom: 4 },
    input: { width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontSize: TYPO.sizes.base, fontFamily: 'inherit', boxSizing: 'border-box' },
    section: { marginBottom: SPACE.lg, paddingBottom: SPACE.md, borderBottom: `1px solid ${COLORS.line}` },
    btn: (kind) => ({
      padding: '8px 14px', fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold,
      borderRadius: RADIUS.sm, cursor: 'pointer', fontFamily: 'inherit',
      border: kind === 'primary' ? 'none' : `1px solid ${COLORS.line}`,
      background: kind === 'primary' ? COLORS.add : (kind === 'danger' ? '#fff' : COLORS.bg),
      color: kind === 'primary' ? '#fff' : (kind === 'danger' ? '#dc2626' : COLORS.text),
    }),
  }

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: SPACE.sm, alignItems: 'center', marginBottom: SPACE.md, flexWrap: 'wrap' }}>
        <button onClick={() => setNewUserOpen(true)} style={styles.btn('primary')}>+ Nový používateľ</button>
        <input
          type="text" placeholder="Hľadať meno / login"
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ ...styles.input, width: 240 }}
        />
        <select value={filterRole} onChange={e => setFilterRole(e.target.value)} style={{ ...styles.input, width: 200 }}>
          <option value="all">Všetky roly</option>
          {ROLES_USABLE.map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: TYPO.sizes.sm, color: COLORS.muted }}>
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} /> aj deaktivovaných
        </label>
        <span style={{ marginLeft: 'auto', fontSize: TYPO.sizes.xs, color: COLORS.muted }}>
          {filtered.length} z {users.length}
        </span>
      </div>

      {/* Master-Detail */}
      <div style={styles.wrap}>
        {/* Master = zoznam */}
        <div style={styles.master}>
          <div style={{ ...styles.toolbar, fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, color: COLORS.muted, textTransform: 'uppercase' }}>
            Zoznam ({filtered.length})
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading
              ? <div style={{ padding: SPACE.md, color: COLORS.muted }}>Načítavam…</div>
              : filtered.length === 0
                ? <div style={{ padding: SPACE.md, color: COLORS.muted }}>Žiadny používateľ.</div>
                : filtered.map(u => {
                  const actions = []
                  actions.push({ label: 'Otvoriť detail', onClick: () => setSelId(u.id) })
                  actions.push({ label: 'Resetnúť heslo', onClick: () => { setSelId(u.id); setResetPwOpen(true); } })
                  if (!PIN_FORBIDDEN_ROLES.includes(u.role)) actions.push({ label: u.has_pin ? 'Zmeniť PIN' : 'Prideliť PIN', onClick: () => { setSelId(u.id); setPinDialog(true); } })
                  if (u.id !== me.id && u.active === 1) actions.push({ label: 'Deaktivovať', icon: '✕', danger: true, onClick: async () => { setSelId(u.id); /* otvor detail, Mirko si potvrdí cez 'Deaktivovať' tlačidlo */ } })
                  return (
                    <div key={u.id} onClick={() => setSelId(u.id)} style={{ ...styles.listItem(u.id === selId), display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: TYPO.weights.semibold, fontSize: TYPO.sizes.base, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          {u.cele_meno || u.display_name || u.username}
                          {u.active === 0 && <span style={{ marginLeft: 6, fontSize: TYPO.sizes.xs, color: '#dc2626' }}>✕ deaktivovaný</span>}
                        </div>
                        <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 2 }}>
                          {u.funkcia ? u.funkcia + ' · ' : ''}{ROLE_LABELS[u.role] || u.role} · <span style={{ fontFamily: TYPO.mono }}>{u.username}</span>
                          {!u.employee_id && !['admin', 'owner', 'owner_view'].includes(u.role) && <span style={{ color: '#dc2626' }}> · bez väzby</span>}
                        </div>
                      </div>
                      <RowMenu actions={actions} compact />
                    </div>
                  )
                })}
          </div>
        </div>

        {/* Detail */}
        <div style={styles.detail}>
          {!draft ? (
            <div style={{ color: COLORS.muted }}>Vyber používateľa zo zoznamu.</div>
          ) : (
            <>
              {/* Profil */}
              <div style={styles.section}>
                <h4 style={{ margin: 0, marginBottom: SPACE.md }}>Profil</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE.md }}>
                  <div>
                    <div style={styles.label}>Meno (zobrazované)</div>
                    <input value={draft.display_name || ''} onChange={e => setDraft({ ...draft, display_name: e.target.value })} style={styles.input} />
                  </div>
                  <div>
                    <div style={styles.label}>Telefón (SMS oznamy — údržba, alerty)</div>
                    <input value={draft.phone || ''} placeholder="+421 9xx xxx xxx" onChange={e => setDraft({ ...draft, phone: e.target.value })} style={styles.input} />
                  </div>
                  <div>
                    <div style={styles.label}>Login (prihlasovacie meno)</div>
                    <input value={draft.username} onChange={e => setDraft({ ...draft, username: e.target.value.toLowerCase().trim() })} disabled={draft.id === me.id} style={{ ...styles.input, background: draft.id === me.id ? COLORS.bgSoft : COLORS.bg, color: draft.id === me.id ? COLORS.muted : COLORS.text }} />
                    {draft.id === me.id && <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 4 }}>Vlastný login meniť nemôžeš.</div>}
                  </div>
                  <div>
                    <div style={styles.label}>Rola</div>
                    <select value={draft.role} onChange={e => setDraft({ ...draft, role: e.target.value })} style={styles.input} disabled={draft.id === me.id}>
                      {ROLES_USABLE.map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
                    </select>
                    {draft.id === me.id && <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 4 }}>Nemôžeš si meniť vlastnú rolu</div>}
                  </div>
                  <div>
                    <div style={styles.label}>Stav</div>
                    <select value={String(draft.active ?? 1)} onChange={e => setDraft({ ...draft, active: Number(e.target.value) })} style={styles.input} disabled={draft.id === me.id}>
                      <option value="1">✓ Aktívny</option>
                      <option value="0">✕ Deaktivovaný</option>
                    </select>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={styles.label}>Zamestnanec z číselníka (väzba je nutná: dochádzka, mzdy, semafor hodinovkárov)</div>
                    <SearchableSelect value={draft.employee_id || ''} onChange={v => setDraft({ ...draft, employee_id: v || null })} options={empOptionsFor(draft.id, draft.employee_id)}
                      placeholder="Vyber zamestnanca…" emptyLabel="Žiadni voľní zamestnanci (všetci už majú konto)" />
                    {!draft.employee_id
                      ? <div style={{ fontSize: TYPO.sizes.xs, color: '#dc2626', marginTop: 4 }}>Bez väzby konto nevidí dochádzku ani mzdy. Jeden zamestnanec = jedno konto.</div>
                      : <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 4 }}>Prepojené: <span style={{ fontFamily: TYPO.mono }}>{draft.employee_id}</span></div>}
                  </div>
                </div>

                {/* Majster — desktop prepínač */}
                {draft.role === 'foreman' && (
                  <div style={{ marginTop: SPACE.md, padding: SPACE.sm, background: COLORS.bgSoft, borderRadius: RADIUS.sm }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: TYPO.sizes.sm }}>
                      <input type="checkbox" checked={!!draft.desktop_enabled} onChange={e => setDraft({ ...draft, desktop_enabled: e.target.checked ? 1 : 0 })} />
                      <span><b>Aktivovať prístup k počítaču (desktop)</b> — predvolene vypnuté</span>
                    </label>
                    <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 4, paddingLeft: 24 }}>
                      Majster štandardne vidí len mobil. Toto zapne aj desktop prístup.
                    </div>
                  </div>
                )}

                {/* Šofér — práva */}
                {draft.role === 'driver' && (
                  <div style={{ marginTop: SPACE.md, padding: SPACE.sm, background: COLORS.bgSoft, borderRadius: RADIUS.sm, fontSize: TYPO.sizes.xs, color: COLORS.muted }}>
                    Šofér má prístup k Vozidlám. Práva na Požičovňu nastavíš po zaplnení Užívateľských práv (zatial v budúcnosti).
                  </div>
                )}

                <div style={{ marginTop: SPACE.md, fontSize: TYPO.sizes.xs, color: COLORS.muted }}>
                  Posledné prihlásenie: {draft.last_login ? new Date(draft.last_login).toLocaleString('sk-SK') : '— nikdy —'}
                </div>
              </div>

              {/* Heslo */}
              <div style={styles.section}>
                <h4 style={{ margin: 0, marginBottom: SPACE.md }}>Heslo</h4>
                <button onClick={() => setResetPwOpen(true)} disabled={draft.id === me.id} style={styles.btn()}>
                  Resetnúť heslo používateľovi
                </button>
                {draft.id === me.id && <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 6 }}>Svoje heslo zmeň cez Heslo v hornej lište.</div>}
              </div>

              {/* PIN */}
              <div style={styles.section}>
                <h4 style={{ margin: 0, marginBottom: SPACE.md }}>PIN (4 čísla, ústava 63)</h4>
                {PIN_FORBIDDEN_ROLES.includes(draft.role) ? (
                  <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.muted }}>
                    Rola „{ROLE_LABELS[draft.role]}" nemá PIN (ústava 63.3 — remeselník nemá PIN).
                  </div>
                ) : (
                  <button onClick={() => setPinDialog(true)} style={styles.btn()}>
                    {draft.has_pin ? 'Zmeniť PIN' : 'Prideliť PIN'}
                  </button>
                )}
              </div>

              {/* Individuálne práva (extra moduly) */}
              <div style={styles.section}>
                <h4 style={{ margin: 0, marginBottom: SPACE.sm }}>Individuálne práva nad rámec roly</h4>
                <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginBottom: SPACE.md }}>
                  Pridaj prístup ku konkrétnym modulom (nad to čo dáva rola). Príklad: Šofér defaultne nevidí Požičovňu, tu ju zaškrtni.
                </div>
                {(() => {
                  const groups = MODULES_ALL.reduce((acc, m) => { (acc[m.group] = acc[m.group] || []).push(m); return acc }, {})
                  const toggle = (id) => {
                    const cur = draft.extra_modules || []
                    setDraft({ ...draft, extra_modules: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] })
                  }
                  return Object.entries(groups).map(([g, mods]) => (
                    <div key={g} style={{ marginBottom: SPACE.sm }}>
                      <div style={{ fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, color: COLORS.muted, textTransform: 'uppercase', marginBottom: 4 }}>{g}</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
                        {mods.map(m => (
                          <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: TYPO.sizes.sm, cursor: 'pointer', padding: '4px 6px', borderRadius: RADIUS.sm, background: (draft.extra_modules || []).includes(m.id) ? COLORS.navBg : 'transparent' }}>
                            <input type="checkbox" checked={(draft.extra_modules || []).includes(m.id)} onChange={() => toggle(m.id)} />
                            {m.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))
                })()}
              </div>

              {/* Akcie */}
              <div style={{ display: 'flex', gap: SPACE.sm, marginTop: SPACE.md }}>
                <button onClick={ulozit} disabled={!dirty || busy} style={{ ...styles.btn('primary'), opacity: (!dirty || busy) ? 0.5 : 1 }}>
                  {busy ? 'Ukladám…' : 'Uložiť'}
                </button>
                <button onClick={() => setDraft({ ...selected })} disabled={!dirty || busy} style={styles.btn()}>↺ Zahodiť zmeny</button>
                <button onClick={zmazat} disabled={busy || draft.id === me.id || draft.active === 0} style={{ ...styles.btn('danger'), marginLeft: 'auto' }}>
                  ✕ Deaktivovať
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modaly */}
      {newUserOpen && <NewUserDialog onClose={() => { setNewUserOpen(false); nacitaj() }} />}
      {resetPwOpen && draft && <ResetPasswordDialog user={draft} onClose={() => setResetPwOpen(false)} onSaved={nacitaj} />}
      {pinDialog && draft && <PinDialog user={draft} onClose={() => setPinDialog(false)} onSaved={nacitaj} />}
    </div>
  )
}

// ─── NEW USER DIALOG ──────────────────────────────────────────
function NewUserDialog({ onClose }) {
  const toast = useToast()
  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [role, setRole] = useState('partak')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [employeeId, setEmployeeId] = useState('')
  const [empOptions, setEmpOptions] = useState([])
  const [technicky, setTechnicky] = useState(false)   // identita_v1: technický účet (asistent) nie je človek — jediná výnimka z väzby

  // Číselník zamestnancov BEZ konta (dedup: 1 zamestnanec = 1 konto, ústava 13)
  useEffect(() => {
    Promise.all([
      fetchAuth('/api/employees').then(r => r.ok ? r.json() : []).catch(() => []),
      fetchAuth('/api/auth/users').then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([emps, users]) => {
      const taken = new Set((users || []).map(u => u.employee_id).filter(Boolean))
      const avail = (emps || []).filter(e => !taken.has(e.id) && e.active !== 0 && e.active !== '0')
      setEmpOptions(avail.map(e => ({
        value: e.id,
        label: (`${e.firstName || ''} ${e.lastName || ''}`.trim() || e.name || e.id) + (e.is_partak ? '' : ''),
        _emp: e,
      })))
    }).catch(() => {})
  }, [])

  const slug = s => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '.').replace(/^\.+|\.+$/g, '')

  const pickEmployee = (id) => {
    setEmployeeId(id)
    const opt = empOptions.find(o => o.value === id)
    if (opt) {
      const e = opt._emp
      const nm = `${e.firstName || ''} ${e.lastName || ''}`.trim() || e.name || ''
      setDisplayName(nm)
      setUsername(slug(`${e.firstName || ''}.${e.lastName || ''}`) || slug(nm))
      if (e.is_partak || (e.role || '').includes('pred')) setRole('partak')
    }
  }

  const save = async () => {
    if (!username.trim() || !displayName.trim() || !password) { toast.error('Vyplň všetky polia'); return }
    if (!employeeId && !technicky) { toast.error('Vyber zamestnanca z číselníka — konto patrí človeku (ústava 136.1). Ak to nie je človek, označ technický účet.'); return }   // identita_v1
    if (password.length < 6) { toast.error('Heslo musí mať aspoň 6 znakov'); return }
    setBusy(true)
    try {
      const r = await fetchAuth('/api/auth/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim().toLowerCase(), display_name: displayName.trim(), role, password, employee_id: employeeId || null, technicky: technicky ? 1 : 0 }),
      })
      const d = await r.json()
      if (!r.ok) {   // identita_v1: 409 = tá istá osoba už konto má — odkaz na existujúce (139), druhé sa nezakladá
        toast.error(d.error || 'Vytvorenie zlyhalo'); setBusy(false)
        if (r.status === 409 && d.odkaz && window.confirm((d.error || 'Konto už existuje.') + '\n\nOtvoriť existujúce konto?')) { try { window.location.assign('/v2' + d.odkaz) } catch (_) {} }
        return
      }
      toast.success('Používateľ vytvorený')
      onClose()
    } catch (e) { toast.error('Chyba: ' + e.message); setBusy(false) }
  }

  return (
    <Modal title="Nový používateľ" onClose={onClose}>
      <Field label="Zamestnanec (z číselníka — predvyplní login + meno)">
        <SearchableSelect value={employeeId} onChange={pickEmployee} options={empOptions}
          placeholder="Vyber zamestnanca…" emptyLabel="Žiadni voľní zamestnanci (všetci už majú konto)" />
      </Field>
      <Field label="Login (prihlasovacie meno)"><input value={username} onChange={e => setUsername(e.target.value)} style={fieldStyle} autoFocus /></Field>
      <Field label="Meno (zobrazované)"><input value={displayName} onChange={e => setDisplayName(e.target.value)} style={fieldStyle} /></Field>
      <Field label="Rola">
        <select value={role} onChange={e => setRole(e.target.value)} style={fieldStyle}>
          {ROLES_USABLE.map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
        </select>
      </Field>
      <Field label="Heslo (najmenej 6 znakov)"><input type="password" value={password} onChange={e => setPassword(e.target.value)} style={fieldStyle} /></Field>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: COLORS.muted, margin: '4px 0 8px' }}>
        <input type="checkbox" checked={technicky} onChange={e => setTechnicky(e.target.checked)} /> Technický účet (nie človek — napr. asistent); bez väzby na zamestnanca
      </label>
      <FooterButtons onCancel={onClose} onSave={save} busy={busy} saveLabel="Vytvoriť" />
    </Modal>
  )
}

// ─── RESET PASSWORD DIALOG ────────────────────────────────────
function ResetPasswordDialog({ user, onClose, onSaved }) {
  const toast = useToast()
  const [pw, setPw] = useState('')
  const [pw2, setPw2] = useState('')
  const [busy, setBusy] = useState(false)

  const save = async () => {
    if (pw.length < 6) { toast.error('Heslo musí mať aspoň 6 znakov'); return }
    if (pw !== pw2) { toast.error('Heslá sa nezhodujú'); return }
    if (!window.confirm(`Naozaj resetnúť heslo používateľovi "${user.username}"?\n\nNové heslo: ${pw}\n\nOdovzdaj ho užívateľovi.`)) return
    setBusy(true)
    try {
      const r = await fetchAuth(`/api/auth/users/${user.id}`, {
        method: 'PUT',
        body: JSON.stringify({ password: pw }),
      })
      const d = await r.json()
      if (!r.ok) { toast.error(d.error || 'Reset hesla zlyhal'); setBusy(false); return }
      toast.success('Heslo úspešne zmenené')
      onSaved?.()
      onClose()
    } catch (e) { toast.error('Chyba: ' + e.message); setBusy(false) }
  }

  return (
    <Modal title={`Reset hesla — ${user.display_name || user.username}`} onClose={onClose}>
      <Field label="Nové heslo (najmenej 6 znakov)"><input type="text" value={pw} onChange={e => setPw(e.target.value)} style={fieldStyle} autoFocus /></Field>
      <Field label="Nové heslo znova"><input type="text" value={pw2} onChange={e => setPw2(e.target.value)} style={fieldStyle} /></Field>
      <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 8 }}>
        Po uložení odovzdaj heslo používateľovi. Operácia je zapísaná do auditu.
      </div>
      <FooterButtons onCancel={onClose} onSave={save} busy={busy} saveLabel="Uložiť heslo" />
    </Modal>
  )
}

// ─── PIN DIALOG ───────────────────────────────────────────────
function PinDialog({ user, onClose, onSaved }) {
  const toast = useToast()
  const [pin, setPin] = useState('')
  const [busy, setBusy] = useState(false)

  const save = async () => {
    if (!/^\d{4}$/.test(pin)) { toast.error('PIN musí byť presne 4 čísla'); return }
    setBusy(true)
    try {
      const r = await fetchAuth(`/api/auth/users/${user.id}/action-pin`, {
        method: 'PUT',
        body: JSON.stringify({ pin }),
      })
      const d = await r.json()
      if (!r.ok) { toast.error(d.error || 'Nastavenie PIN zlyhalo'); setBusy(false); return }
      toast.success('PIN nastavený')
      onSaved?.()
      onClose()
    } catch (e) { toast.error('Chyba: ' + e.message); setBusy(false) }
  }

  return (
    <Modal title={`PIN — ${user.display_name || user.username}`} onClose={onClose}>
      <Field label="PIN (presne 4 čísla)">
        <input
          type="text" inputMode="numeric" pattern="\d{4}" maxLength={4}
          value={pin}
          onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
          style={{ ...fieldStyle, fontFamily: TYPO.mono, fontSize: TYPO.sizes.lg, letterSpacing: '0.4em', textAlign: 'center' }}
          autoFocus
        />
      </Field>
      <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 8 }}>
        PIN slúži na autorizáciu citlivých operácií (ústava 63 — anti-fraud, nie login). Operácia je zapísaná do auditu.
      </div>
      <FooterButtons onCancel={onClose} onSave={save} busy={busy} saveLabel="Uložiť PIN" />
    </Modal>
  )
}

// ─── Helper komponenty (modal/field/footer) ──────────────────
const fieldStyle = {
  width: '100%', padding: '9px 11px', border: `1px solid ${COLORS.line}`,
  borderRadius: RADIUS.sm, fontSize: TYPO.sizes.base, fontFamily: 'inherit', boxSizing: 'border-box',
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: SPACE.md }}>
      <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  )
}

function Modal({ title, children, onClose }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: SPACE.md,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        width: 460, maxWidth: '100%', background: COLORS.bg, borderRadius: RADIUS.lg,
        padding: SPACE.lg, fontFamily: TYPO.family,
      }}>
        <h3 style={{ margin: 0, marginBottom: SPACE.md }}>{title}</h3>
        {children}
      </div>
    </div>
  )
}

function FooterButtons({ onCancel, onSave, busy, saveLabel = 'Uložiť' }) {
  return (
    <div style={{ display: 'flex', gap: SPACE.sm, justifyContent: 'flex-end', marginTop: SPACE.lg }}>
      <button onClick={onCancel} disabled={busy} style={{
        padding: '9px 18px', border: `1px solid ${COLORS.line}`, background: '#fff',
        borderRadius: RADIUS.md, fontSize: TYPO.sizes.base, cursor: 'pointer', fontFamily: 'inherit',
      }}>Zrušiť</button>
      <button onClick={onSave} disabled={busy} style={{
        padding: '9px 18px', border: 'none', background: COLORS.add, color: '#fff',
        borderRadius: RADIUS.md, fontSize: TYPO.sizes.base, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700,
      }}>{busy ? 'Ukladám…' : saveLabel}</button>
    </div>
  )
}
